import React, { useEffect, useState } from "react";
import { Box, Stack, Switch, Typography } from "@mui/material";
import cronstrue from "cronstrue/i18n";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { Features } from "../../../Enums/companyStatus";
import { hasPermission } from "../../../Helpers/authHelpers";
import { VoicesSchedule } from "../../../Models/voicesConfig";
import { useUpdateVoicesSchedule, useVoicesSchedules } from "../../../Query/useVoicesConfig";
import { ComoButton } from "../../Common/ComoButton";
import VoicesCard from "../Common/VoicesCard";
import VoicesInput from "../Common/VoicesInput";

const formatTime = (value: string | null) =>
  value ? new Date(value).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : "–";

const CRON_FIELDS = 5;

/** The cron in words, or null when it is not a five-field cron the scheduler accepts. */
const describeCron = (expression: string, language: string): string | null => {
  const trimmed = expression.trim();
  if (trimmed.split(/\s+/).length !== CRON_FIELDS) return null;
  try {
    return cronstrue.toString(trimmed, {
      locale: language.startsWith("de") ? "de" : "en",
      use24HourTimeFormat: true,
      throwExceptionOnParseError: true,
    });
  } catch {
    return null;
  }
};

const VoicesScheduleRow: React.FC<{ schedule: VoicesSchedule; editable: boolean }> = ({
  schedule,
  editable,
}) => {
  const { t, i18n } = useTranslation("voicesSettings");
  const updateSchedule = useUpdateVoicesSchedule();
  const [cron, setCron] = useState(schedule.cron_expression ?? "");
  const description = describeCron(cron, i18n.language);

  useEffect(() => setCron(schedule.cron_expression ?? ""), [schedule.cron_expression]);

  const save = (data: { cron_expression?: string; enabled?: boolean }) =>
    updateSchedule.mutate({ taskKey: schedule.task_key, data });

  return (
    <Box sx={{ p: 2, borderRadius: 2, border: `1px solid ${color.gray200}` }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t(`schedule_${schedule.task_key}`)}
          </Typography>
          <Typography variant="caption">{t(`schedule_${schedule.task_key}_hint`)}</Typography>
        </Box>
        {schedule.active ? (
          <Switch
            checked={schedule.enabled}
            disabled={!editable || updateSchedule.isPending}
            onChange={(e) => save({ enabled: e.target.checked })}
            sx={{
              "& .Mui-checked": { color: `${color.orange600} !important` },
              "& .Mui-checked + .MuiSwitch-track": {
                backgroundColor: `${color.orange600} !important`,
              },
            }}
          />
        ) : editable ? (
          <ComoButton
            onClick={() => save({ cron_expression: cron.trim(), enabled: true })}
            disabled={!description || updateSchedule.isPending}
          >
            {t("scheduleActivate")}
          </ComoButton>
        ) : (
          <Typography variant="caption">{t("scheduleNotActive")}</Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1.5, flexWrap: "wrap" }}>
        <VoicesInput
          size="small"
          value={cron}
          disabled={!editable}
          onChange={(e) => setCron(e.target.value)}
          error={!description}
          inputProps={{ "aria-label": t("scheduleCron") }}
          sx={{ width: 180 }}
        />
        {editable && schedule.active && cron.trim() !== (schedule.cron_expression ?? "") && (
          <ComoButton
            onClick={() => save({ cron_expression: cron.trim() })}
            disabled={!description || updateSchedule.isPending}
          >
            {t("save")}
          </ComoButton>
        )}
        <Typography
          sx={{
            flexBasis: "100%",
            fontSize: 12,
            color: description ? color.gray600 : color.red700,
          }}
        >
          {description ? t("scheduleRuns", { when: description }) : t("scheduleCronInvalid")}
        </Typography>
        {schedule.active && (
          <Typography variant="caption">
            {t("scheduleNext", {
              time: schedule.enabled ? formatTime(schedule.next_run_at) : "–",
            })}
            {" · "}
            {t("scheduleLast", { time: formatTime(schedule.last_run_at) })}
            {schedule.last_status ? ` (${schedule.last_status})` : ""}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const VoicesSchedules: React.FC = () => {
  const { t } = useTranslation("voicesSettings");
  const { data: schedules = [] } = useVoicesSchedules();
  const editable = hasPermission(Features.VOICES_MARKETING);

  return (
    <VoicesCard
      title={t("schedulesTitle")}
      subtitle={editable ? t("schedulesSubtitleAdmin") : t("schedulesSubtitle")}
    >
      <Stack spacing={1.5}>
        {schedules.map((schedule) => (
          <VoicesScheduleRow key={schedule.task_key} schedule={schedule} editable={editable} />
        ))}
      </Stack>
    </VoicesCard>
  );
};

export default VoicesSchedules;
